import {useEffect, useState} from "react";
import {deepPatch} from "./DeepPatchUtil";
import {noop} from "./FunctionUtil";
import {withRefreshIndicator} from "../components/refresh-indicator/refreshIndicatorStore";

export class RemoteValue<T> {
    private _promise?: Promise<T>
    private _resolvedValue?: T;

    constructor(
        private readonly fetch: () => Promise<T>,
        optimisticValue?: T | Promise<T>,
    ) {
        if (optimisticValue) {
            if (optimisticValue instanceof Promise) {
                this._promise = optimisticValue;
                this._promise.then(value => this._resolvedValue = value);
            } else {
                this._promise = Promise.resolve(optimisticValue);
                this._resolvedValue = optimisticValue;
            }
        }
    }

    private get lazyPromise(): Promise<T> {
        if (!this._promise) {
            this._promise = this.fetch();
            this._promise.then(value => this._resolvedValue = value);
        }
        return this._promise;
    }

    get value(): T | Promise<T> {
        return this._resolvedValue || this.lazyPromise;
    }

    resolvedValueOr(defaultValue: T): T {
        return this._resolvedValue !== undefined ? this._resolvedValue : defaultValue;
    }

    eagerLoad() {
        if (!this._resolvedValue) {
            this.lazyPromise.then(noop);
        }
    }

    resolve<S>(onFulfilled: (value: T) => S | Promise<S>): S | Promise<S> {
        if (this._resolvedValue) {
            return onFulfilled(this._resolvedValue);
        } else {
            return this.lazyPromise.then(onFulfilled);
        }
    }

    patch(patch: (oldValue: T) => T): RemoteValue<T> {
        const oldValue = this._resolvedValue;
        if (oldValue) {
            return new RemoteValue(this.fetch, patch(oldValue))
        } else {
            return this.refresh();
        }
    }

    refresh(): RemoteValue<T> {
        return new RemoteValue(this.fetch);
    }

    async patchRemote(newRemoteValue: RemoteValue<T>): Promise<RemoteValue<T>> {
        const oldValue = this._resolvedValue;
        if (!oldValue) {
            return newRemoteValue;
        } else {
            return await withRefreshIndicator(async () => {
                const newValue = await newRemoteValue.value;
                const patchedValue = deepPatch(oldValue, newValue as T);
                if (patchedValue === oldValue) {
                    return this;
                } else {
                    return new RemoteValue(this.fetch, patchedValue);
                }
            });
        }
    }

    map<V>(transform: (value: T) => V | Promise<V>): RemoteValue<V> {
        return new RemoteValue<V>(
            () => this.lazyPromise.then(transform),
            this._resolvedValue ? transform(this._resolvedValue) : undefined,
        );
    }

    static combine<Obj extends {}>(a: { [K in keyof Obj]: RemoteValue<Obj[K]> }): RemoteValue<Obj> {
        const entries = Object.entries(a) as [keyof Obj, RemoteValue<Obj[keyof Obj]>][];
        return new RemoteValue<Obj>(
            async () => {
                const awaitedEntries = await Promise.all(entries.map(async ([key, value]) => [key, await value.value]));
                return Object.fromEntries(awaitedEntries) as Obj;
            },
            entries.every(([_, value]) => value._resolvedValue)
                ? Object.fromEntries(entries.map(([key, value]) => [key, value._resolvedValue])) as Obj
                : undefined
        );
    }

    static fromValue<T>(value: T): RemoteValue<T> {
        return new RemoteValue(() => Promise.resolve(value), value);
    }
}

export function useRemoteValue<T>(remoteValue: RemoteValue<T>, defaultValue: T): T {
    const [value, setValue] = useState(remoteValue.resolvedValueOr(defaultValue));
    useEffect(() => {
        remoteValue.resolve(value => {
            setValue(value)
        })
    }, [remoteValue, setValue]);
    return value;
}
